import Divider from '@mui/material/Divider';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import Radio from '@mui/material/Radio';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Grid from '@mui/material/Grid2';
import Autocomplete from '@mui/material/Autocomplete';
import { GenericDialog } from '../../GenericDialog';
import { containedButtonStyle, textButtonStyle } from '@/theme';
import { DatePickerIcon } from '@/assets/icons/DatePickerIcon';
import { formatEnumForDisplay } from '@/utils';
import { ReportActionStatus, ReportInternalType } from '@/__generated__/graphql';
import {
  ReportDatePicker,
  ReportFormRadioGroup,
  ReportFormTextField,
  autocompleteTextFieldStyles,
  datePickerSlotProps,
  contentStyles,
  paperStyles,
  titleStyles,
} from '../styles';
import { useReportFormLogic } from '@/components/Analysis/ReportsRevamped/hooks/useReportFormLogic';

type Props = {
  open: boolean;
  title: string;
  handleClose: () => void;
  selectedPointOfInterestIds: string[];
  reportCreated?: () => void;
  internalType: ReportInternalType;
  componentIds?: string[];
};

const actionStatuses = [
  ReportActionStatus.Backlog,
  ReportActionStatus.InProgress,
  ReportActionStatus.Complete,
];

export const AddToComponentReport = ({
  open,
  title,
  selectedPointOfInterestIds,
  handleClose,
  reportCreated = () => {},
  internalType,
  componentIds,
}: Props) => {
  const {
    reportState,
    reportName,
    actionStatus,
    dueDate,
    isFocused,
    reportId,
    mappedDraftReports,
    getReportNameError,
    handleRadioChanged,
    handleReportNameChange,
    handleWorkpackNumberChange,
    handleReportDueDateChange,
    handleReportActionStatusChange,
    handleReportIdChanged,
    setIsFocused,
    isInValidReportEntry,
    handleAddOrCreateReport,
    submittingReportState,
  } = useReportFormLogic({
    selectedPointOfInterestIds,
    handleClose,
    reportCreated,
    internalType,
    componentIds,
  });

  return (
    <GenericDialog
      open={open}
      onClose={handleClose}
      showCloseIcon
      title={title}
      paperStyles={paperStyles}
      contentStyles={contentStyles}
      titleStyles={titleStyles}
      content={
        <>
          <Stack spacing={3}>
            <FormControl fullWidth sx={{ marginBottom: 3 }}>
              <ReportFormRadioGroup
                defaultValue="create"
                onChange={handleRadioChanged}
                value={reportState}
                row
              >
                <Grid container sx={{ width: '100%' }}>
                  <Grid size={{ xs: 6 }}>
                    <FormControlLabel
                      value="create"
                      control={<Radio />}
                      label="Create new report"
                    />
                  </Grid>
                  <Grid size={{ xs: 6 }}>
                    <FormControlLabel
                      value="edit"
                      control={<Radio />}
                      label="Add to existing report"
                    />
                  </Grid>
                </Grid>
              </ReportFormRadioGroup>
            </FormControl>

            {reportState === 'create' ? (
              <Stack gap={5}>
                <ReportFormTextField
                  label="Report name"
                  variant="standard"
                  fullWidth
                  error={isFocused && !!getReportNameError(reportName)}
                  helperText={isFocused ? getReportNameError(reportName) : ''}
                  onChange={handleReportNameChange}
                  onFocus={() => setIsFocused(true)}
                  slotProps={{
                    input: {
                      inputProps: {
                        maxLength: 200,
                      },
                    },
                  }}
                />

                <Grid container columnSpacing={3} rowSpacing={2}>
                  <Grid size={{ xs: 6 }}>
                    <ReportFormTextField
                      label="Workpack number"
                      variant="standard"
                      fullWidth
                      size="small"
                      onChange={handleWorkpackNumberChange}
                    />
                  </Grid>
                  <Grid size={{ xs: 6 }}>
                    <Stack alignItems="end" direction="row" height="100%">
                      <ReportDatePicker
                        format="dd MMM yyyy"
                        disablePast
                        label="Due date"
                        slotProps={datePickerSlotProps}
                        onChange={handleReportDueDateChange}
                        value={dueDate ? new Date(dueDate) : undefined}
                        slots={{
                          openPickerIcon: DatePickerIcon,
                        }}
                      />
                    </Stack>
                  </Grid>
                  <Grid size={{ xs: 6 }}>
                    <Autocomplete
                      value={formatEnumForDisplay(actionStatus)}
                      options={actionStatuses.map((status) => formatEnumForDisplay(status))}
                      disableClearable
                      onChange={handleReportActionStatusChange}
                      renderInput={(parameters) => (
                        <TextField
                          {...parameters}
                          label="Status"
                          variant="standard"
                          sx={autocompleteTextFieldStyles}
                        />
                      )}
                    />
                  </Grid>
                </Grid>
              </Stack>
            ) : (
              <Autocomplete
                options={mappedDraftReports}
                fullWidth
                disableListWrap
                autoHighlight
                onChange={handleReportIdChanged}
                renderInput={(parameters) => (
                  <TextField
                    error={reportId === ''}
                    helperText={reportId === '' ? 'Please select a report...' : ''}
                    {...parameters}
                    label="Report Name"
                    data-testid="report-selector"
                  />
                )}
              />
            )}
          </Stack>
          <Divider sx={{ mt: 3, mb: 2 }} />
        </>
      }
      actions={
        <>
          <Button onClick={handleClose} variant="text" sx={textButtonStyle}>
            Cancel
          </Button>
          <Button
            onClick={handleAddOrCreateReport}
            sx={containedButtonStyle}
            disabled={isInValidReportEntry || submittingReportState === 'Loading'}
            variant="contained"
          >
            {reportState === 'create' ? 'Create Report' : 'Add to Report'}
          </Button>
        </>
      }
    />
  );
};
